// src/utils/llmAdapter.ts

import { Citation } from '../components/Citations';

export interface NormalizedLLMMessage {
  text: string | null;
  chart: any | null;
  citations: Citation[] | null;
}

function unescapeJsonStringFragment(value: string): string {
  const placeholder = '__JSON_BACKSLASH__';
  return value
    .replace(/\\\\/g, placeholder)
    .replace(/\\"/g, '"')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t')
    .replace(new RegExp(placeholder, 'g'), '\\');
}

/**
 * During streaming we can receive partial canonical JSON like:
 * {"text":"partial ...   (not closed yet)
 * Extract and show only the text portion so users don't see raw JSON.
 */
function extractTextFromPartialCanonicalJson(raw: string): string | null {
  const trimmed = raw.trimStart();
  if (!trimmed.startsWith('{') || !trimmed.includes('"text"')) return null;

  const keyIdx = trimmed.indexOf('"text"');
  if (keyIdx === -1) return null;
  const colonIdx = trimmed.indexOf(':', keyIdx);
  if (colonIdx === -1) return null;

  let start = colonIdx + 1;
  while (start < trimmed.length && /\s/.test(trimmed[start])) start++;
  if (start >= trimmed.length || trimmed[start] !== '"') return null;
  start += 1;

  let i = start;
  let closed = false;
  while (i < trimmed.length) {
    const ch = trimmed[i];
    if (ch === '\\') {
      i += 2;
      continue;
    }
    if (ch === '"') {
      closed = true;
      break;
    }
    i += 1;
  }

  const fragment = closed ? trimmed.slice(start, i) : trimmed.slice(start);
  return unescapeJsonStringFragment(fragment);
}

/**
 * Normalizes any LLM/backend response into a predictable shape
 * Handles:
 *  - plain text
 *  - JSON object
 *  - stringified JSON
 */
export function normalizeLLMMessage(raw: any): NormalizedLLMMessage {
 
  // Case 1: already an object (ideal backend)
  if (typeof raw === 'object' && raw !== null) {
    return {
      text: raw.text ?? null,
      chart: raw.chart ?? null,
      citations: raw.citations ?? null
    };
  }

  // Case 2: stringified JSON
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          text: parsed.text ?? null,
          chart: parsed.chart ?? null,
          citations: parsed.citations ?? null
        };
      }
    } catch {
      // Could be partial streaming JSON; try to recover text.
      const partialText = extractTextFromPartialCanonicalJson(raw);
      if (partialText !== null) {
        return {
          text: partialText,
          chart: null,
          citations: null,
        };
      }
    }
  }

  // Case 3: plain text
  return {
    text: String(raw),
    chart: null,
    citations: null
  };
}
